/**
 * explainerHintsPref — user preference for the inline explainer / help "hint"
 * cards (the dark card with the teal book icon + "Open full guide" link +
 * dismiss ✕, rendered by `src/components/reportGuide/ContextualGuideTip.tsx`
 * above various sections).
 *
 * Two pieces of state, both in localStorage so they survive sign-out / reload
 * and never roundtrip through Firestore (pure UX preference, no audit value):
 *
 *  - `da-explainer-hints` = 'on' | 'off' — the user's EXPLICIT choice from the
 *    UserMenu toggle. Absent (null) = the user has never made a choice.
 *  - `da-login-count` = integer — how many times the user has loaded the app
 *    while authenticated (bumped once per session by `recordLogin()`).
 *
 * Resolution (`shouldShowExplainerHints`): an explicit pref always wins. With
 * no explicit pref, hints are ON for the first 5 logins and default OFF after
 * — so new users get the onboarding help, then it quietly steps out of the way.
 *
 * Mirrors the localStorage pattern of `src/lib/accentPalette.ts` /
 * `src/lib/lastSubTabMemory.ts`: every accessor is wrapped in try/catch so a
 * private-mode / sandboxed-webview storage throw degrades to a safe default
 * (we'd rather lose the preference than crash the page).
 */

export const EXPLAINER_HINTS_KEY = 'da-explainer-hints';
export const LOGIN_COUNT_KEY = 'da-login-count';

/** Logins for which hints stay ON by default (until the user toggles). */
export const DEFAULT_ON_LOGIN_LIMIT = 5;

/**
 * Fired on `window` whenever the pref changes so already-mounted hint cards
 * (and the menu toggle) re-read and re-render without a reload. The native
 * `storage` event only fires in OTHER tabs, so we dispatch this explicitly in
 * the same tab.
 */
export const EXPLAINER_HINTS_CHANGED_EVENT = 'da-explainer-hints-changed';

type ExplainerHintsPref = 'on' | 'off';

function safeGetItem(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null; // private mode / blocked storage
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — losing the preference is fine */
  }
}

/**
 * The user's EXPLICIT toggle choice, or `null` when they've never chosen.
 * Anything other than 'on' / 'off' is treated as unset.
 */
export function getExplainerHintsPref(): ExplainerHintsPref | null {
  const raw = safeGetItem(EXPLAINER_HINTS_KEY);
  return raw === 'on' || raw === 'off' ? raw : null;
}

/** Persist the explicit toggle choice. */
export function setExplainerHintsPref(on: boolean): void {
  safeSetItem(EXPLAINER_HINTS_KEY, on ? 'on' : 'off');
}

/** Current login count (≥ 0). Non-numeric / absent storage reads as 0. */
function getLoginCount(): number {
  const raw = safeGetItem(LOGIN_COUNT_KEY);
  const n = raw === null ? 0 : Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Increment the login count by one. Call ONCE per authenticated app
 * load/session (guarded by the caller so it doesn't fire on every render).
 */
export function recordLogin(): void {
  safeSetItem(LOGIN_COUNT_KEY, String(getLoginCount() + 1));
}

/**
 * Whether the inline explainer/hint cards should render right now.
 *
 *  - Explicit user choice wins: 'on' → true, 'off' → false.
 *  - Otherwise default ON for the first {@link DEFAULT_ON_LOGIN_LIMIT} logins,
 *    then OFF.
 */
export function shouldShowExplainerHints(): boolean {
  const pref = getExplainerHintsPref();
  if (pref !== null) return pref === 'on';
  return getLoginCount() <= DEFAULT_ON_LOGIN_LIMIT;
}

/**
 * Notify same-tab listeners that the pref changed. Call after
 * `setExplainerHintsPref`. Wrapped because some embedded webviews lack a usable
 * `window`/`dispatchEvent`.
 */
export function notifyExplainerHintsChanged(): void {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(EXPLAINER_HINTS_CHANGED_EVENT));
  } catch {
    /* no-op */
  }
}
